import React, { useMemo, useState } from 'react'
import {
  Loader2,
  Sparkles,
  Shield,
  BookOpen,
  RefreshCcw,
  Clock,
  BarChart3,
  Plus,
  Minus,
  AlertTriangle
} from 'lucide-react'
import Navigation from '../../components/common/Navigation'
import Footer from '../../components/common/Footer'
import QuizInterface from '../../components/quiz/QuizInterface'
import { examGeneratorService } from '../../services/exam/ExamGeneratorService'
import { BankQuizData } from '../../services/bankQuestionService_moi'

type ExamType = 'GKI' | 'GKII' | 'CKI' | 'CKII'

const EXAM_OPTIONS: Record<ExamType, { title: string; description: string; chapters: string; color: string }> = {
  GKI: {
    title: 'Giữa kỳ I',
    description: 'Tự động lấy câu hỏi từ Chương I (Dao động) với đúng tỉ lệ TN/DS/TL.',
    chapters: 'Chương 1',
    color: 'from-pink-500 to-rose-500'
  },
  CKI: {
    title: 'Cuối kỳ I',
    description: 'Phủ toàn bộ Chương I & II với 12 TN, 4 ĐS, 6 TL theo chuẩn.',
    chapters: 'Chương 1-2',
    color: 'from-blue-500 to-indigo-500'
  },
  GKII: {
    title: 'Giữa kỳ II',
    description: 'Tập trung vào Chương III (Điện trường) với phân bổ điểm cố định.',
    chapters: 'Chương 3',
    color: 'from-violet-500 to-purple-500'
  },
  CKII: {
    title: 'Cuối kỳ II',
    description: 'Bao quát Chương III & IV, 22 câu đúng cơ cấu M1/M2/M3.',
    chapters: 'Chương 3-4',
    color: 'from-emerald-500 to-teal-500'
  }
}

const GROUP_LABELS = {
  TN: { title: 'Trắc nghiệm', subtitle: '0.25 đ/câu' },
  DS: { title: 'Đúng/Sai', subtitle: '1 đ/câu (4 mệnh đề)' },
  TL: { title: 'Trả lời ngắn', subtitle: '0.5 đ/câu' },
  TLU: { title: 'Tự luận', subtitle: '1.0 đ/câu' }
}

const GROUP_SCORE: Record<'TN' | 'DS' | 'TL' | 'TLU', number> = {
  TN: 0.25,
  DS: 1,
  TL: 0.5,
  TLU: 1.0
}

const DIFFICULTY_LABELS = {
  M1: 'Nhận biết',
  M2: 'Thông hiểu',
  M3: 'Vận dụng'
}

const GROUP_FALLBACK_MAP: Record<string, 'TN' | 'DS' | 'TL' | 'TLU'> = {
  trac_nghiem_nhieu_lua_chon: 'TN',
  multiple_choice: 'TN',
  dung_sai: 'DS',
  true_false: 'DS',
  tinh_toan: 'TL',
  short_answer: 'TL'
}

const formatScore = (value: number) => {
  const fixed = value.toFixed(2)
  if (fixed.endsWith('.00')) return fixed.replace('.00', '')
  if (fixed.endsWith('0')) return fixed.replace(/0$/, '')
  return fixed
}

export const DeTuDongPage: React.FC = () => {
  const [selectedExam, setSelectedExam] = useState<ExamType>('GKI')
  const [selectedLop, setSelectedLop] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [quizData, setQuizData] = useState<BankQuizData | null>(null)
  const [showQuiz, setShowQuiz] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [planInputs, setPlanInputs] = useState<Record<'TN' | 'DS' | 'TL' | 'TLU', Record<'M1' | 'M2' | 'M3', number>>>({
    TN: { M1: 8, M2: 3, M3: 1 },
    DS: { M1: 1, M2: 1, M3: 0 },
    TL: { M1: 2, M2: 2, M3: 0 },
    TLU: { M1: 1, M2: 1, M3: 1 }
  })

  const handleGenerate = async () => {
    if (!selectedLop) {
      setError('Vui lòng chọn lớp trước khi làm bài.')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    setShowQuiz(false)
    try {
      const planOverrides = (Object.keys(planInputs) as ('TN' | 'DS' | 'TL' | 'TLU')[]).reduce((acc, group) => {
        const distribution = planInputs[group]
        const total = Object.values(distribution).reduce((sum, value) => sum + (Number.isFinite(value) ? value : 0), 0)
        acc[group] = {
          total,
          distribution: { ...distribution }
        }
        return acc
      }, {} as Record<'TN' | 'DS' | 'TL' | 'TLU', { total: number; distribution: Record<'M1' | 'M2' | 'M3', number> }>)

      const hasZeroTotal = Object.values(planOverrides).some(plan => plan.total === 0)
      if (hasZeroTotal) {
        setError('Mỗi phần TN/ĐS/TL phải có ít nhất 1 câu. Vui lòng điều chỉnh lại.')
        setLoading(false)
        return
      }

      const result = await examGeneratorService.generateStandardExam({
        examType: selectedExam,
        lop: selectedLop,
        planOverrides
      })
      setQuizData(result)
      // Mở giao diện làm bài ngay sau khi sinh đề
      setShowQuiz(true)
    } catch (err) {
      console.error('❌ Lỗi khi sinh đề tự động:', err)
      setError(err instanceof Error ? err.message : 'Không thể sinh đề. Vui lòng thử lại.')
    } finally {
      setLoading(false)
    }
  }

  const summary = useMemo(() => {
    if (!quizData) return null

    const base = {
      TN: { count: 0, difficulties: { M1: 0, M2: 0, M3: 0 } },
      DS: { count: 0, difficulties: { M1: 0, M2: 0, M3: 0 } },
      TL: { count: 0, difficulties: { M1: 0, M2: 0, M3: 0 } },
      TLU: { count: 0, difficulties: { M1: 0, M2: 0, M3: 0 } }
    }

    quizData.cauHoi.forEach(question => {
      const group =
        question.examMetadata?.group ||
        GROUP_FALLBACK_MAP[question.type as keyof typeof GROUP_FALLBACK_MAP] ||
        'TN'
      const difficulty = question.examMetadata?.difficulty || question.muc_do || 'M1'

      base[group].count += 1
      if (difficulty === 'M1' || difficulty === 'M2' || difficulty === 'M3') {
        base[group].difficulties[difficulty] += 1
      }
    })

    return base
  }, [quizData])

  const actualScores = useMemo(() => {
    if (!summary) return null
    const counts = {
      TN: summary.TN.count,
      DS: summary.DS.count,
      TL: summary.TL.count,
      TLU: summary.TLU.count
    }
    const perGroup = {
      TN: counts.TN * GROUP_SCORE.TN,
      DS: counts.DS * GROUP_SCORE.DS,
      TL: counts.TL * GROUP_SCORE.TL,
      TLU: counts.TLU * GROUP_SCORE.TLU
    }
    const total = perGroup.TN + perGroup.DS + perGroup.TL + perGroup.TLU
    return { counts, perGroup, total }
  }, [summary])

  const handleStart = () => {
    if (!quizData) return
    setShowQuiz(true)
  }

  const planTotals = useMemo(() => {
    const totals: Record<'TN' | 'DS' | 'TL' | 'TLU', number> = { TN: 0, DS: 0, TL: 0, TLU: 0 }
      ; (Object.keys(planInputs) as ('TN' | 'DS' | 'TL' | 'TLU')[]).forEach(group => {
        totals[group] = Object.values(planInputs[group]).reduce((sum, value) => sum + value, 0)
      })
    return totals
  }, [planInputs])

  const planScores = useMemo(() => {
    const perGroup = {
      TN: planTotals.TN * GROUP_SCORE.TN,
      DS: planTotals.DS * GROUP_SCORE.DS,
      TL: planTotals.TL * GROUP_SCORE.TL,
      TLU: planTotals.TLU * GROUP_SCORE.TLU
    }
    const total = perGroup.TN + perGroup.DS + perGroup.TL + perGroup.TLU
    return { perGroup, total }
  }, [planTotals])

  const planWarning = planScores.total > 10

  const handlePlanChange = (group: 'TN' | 'DS' | 'TL' | 'TLU', diff: 'M1' | 'M2' | 'M3', value: number) => {
    const sanitized = Math.max(0, Math.floor(Number.isFinite(value) ? value : 0))
    setPlanInputs(prev => ({
      ...prev,
      [group]: {
        ...prev[group],
        [diff]: sanitized
      }
    }))
  }

  const adjustPlan = (group: 'TN' | 'DS' | 'TL' | 'TLU', diff: 'M1' | 'M2' | 'M3', delta: number) => {
    const current = planInputs[group][diff]
    handlePlanChange(group, diff, Math.max(0, current + delta))
  }

  const handleExitQuiz = () => {
    setShowQuiz(false)
    setIsFullscreen(false)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {!showQuiz && !isFullscreen && <Navigation />}

      {showQuiz && quizData ? (
        <QuizInterface
          quizData={quizData}
          lessonId={`AUTO_${selectedExam}`}
          onBack={handleExitQuiz}
          onFullscreenChange={setIsFullscreen}
        />
      ) : (
        <div className="container mx-auto px-4 py-8">
          <header className="text-center mb-10">
            <p className="text-sm uppercase tracking-widest text-blue-500 font-semibold mb-2">
              Đề kiểm tra tự động
            </p>
            <h1 className="text-4xl font-black text-gray-900 mb-3 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              Sinh đề GKI / GKII / CKI / CKII chỉ với 1 cú click
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Bạn có thể tự điều chỉnh số câu cho từng phần TN, Đúng/Sai, Tự luận (mặc định 12-4-6 với phân bố M1/M2/M3).
              Điểm số và phân tích AI sẽ khớp với cấu hình mới.
            </p>
          </header>

          <div className="max-w-2xl mx-auto mb-10">
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                <BookOpen className="w-4 h-4 inline mr-2" />
                Chọn lớp
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[10, 11, 12].map(lop => (
                  <button
                    key={lop}
                    onClick={() => setSelectedLop(lop)}
                    className={`px-6 py-4 rounded-xl font-semibold text-lg transition-all duration-200 ${selectedLop === lop
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg scale-105'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                      }`}
                  >
                    Lớp {lop}
                  </button>
                ))}
              </div>
              {!selectedLop && (
                <p className="mt-3 text-sm text-amber-600 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Vui lòng chọn lớp trước khi làm bài
                </p>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-4 mb-10">
            {(Object.keys(EXAM_OPTIONS) as ExamType[]).map(exam => {
              const option = EXAM_OPTIONS[exam]
              const isActive = selectedExam === exam

              return (
                <button
                  key={exam}
                  onClick={() => setSelectedExam(exam)}
                  className={`relative overflow-hidden rounded-2xl p-6 text-left border transition-all duration-300 ${isActive ? 'border-transparent shadow-xl scale-[1.01]' : 'border-gray-200 hover:-translate-y-1'
                    }`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${option.color} opacity-10 pointer-events-none`} />
                  <div className="relative z-10 space-y-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white/80 text-gray-800 shadow-sm">
                      {option.chapters}
                    </span>
                    <h3 className="text-xl font-bold text-gray-900">{option.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{option.description}</p>
                    {isActive && (
                      <div className="inline-flex items-center space-x-1 text-sm font-semibold text-blue-600">
                        <Sparkles className="w-4 h-4" />
                        <span>Đang chọn</span>
                      </div>
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 mb-8">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div>
                <div className="inline-flex items-center space-x-2 text-blue-600 font-semibold mb-2">
                  <Shield className="w-5 h-5" />
                  <span>Phân bổ chuẩn Bộ tiêu chí</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Tự động chia đều câu hỏi theo chương & mức độ</h2>
                <p className="text-gray-600 mt-2">
                  12 TN (0.25đ/câu), 4 Đúng/Sai (1đ/câu), 4 Trả lời ngắn (0.5đ/câu) và 3 Tự luận (1.0đ/câu).
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <button
                  onClick={handleGenerate}
                  disabled={loading || !selectedLop}
                  className="inline-flex items-center justify-center px-8 py-3 rounded-2xl font-semibold text-white bg-gradient-to-r from-green-600 to-green-700 shadow-lg hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Đang chuẩn bị...
                    </>
                  ) : (
                    <>
                      <BookOpen className="w-5 h-5 mr-2" />
                      Làm bài
                    </>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl">
                {error}
              </div>
            )}

            {!quizData && (
              <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <span>Tùy chỉnh số câu từng phần</span>
                </h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {(Object.keys(GROUP_LABELS) as ('TN' | 'DS' | 'TL' | 'TLU')[]).map(group => (
                    <div key={group} className="rounded-xl border border-gray-100 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-xs uppercase text-gray-500">{GROUP_LABELS[group].subtitle}</p>
                          <h4 className="font-semibold text-gray-900">{GROUP_LABELS[group].title}</h4>
                        </div>
                        <span className="text-sm font-medium text-blue-600">{planTotals[group]} câu</span>
                      </div>
                      <div className="space-y-3">
                        {(Object.keys(DIFFICULTY_LABELS) as ('M1' | 'M2' | 'M3')[]).map(diff => (
                          <div key={diff} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                            <span className="text-sm text-gray-600">{DIFFICULTY_LABELS[diff]}</span>
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => adjustPlan(group, diff, -1)}
                                className="p-1 rounded-full bg-white border text-gray-600 hover:bg-gray-50"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <input
                                type="number"
                                min={0}
                                value={planInputs[group][diff]}
                                onChange={e => handlePlanChange(group, diff, Number(e.target.value))}
                                className="w-14 text-center border border-gray-200 rounded-lg text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => adjustPlan(group, diff, 1)}
                                className="p-1 rounded-full bg-white border text-gray-600 hover:bg-gray-50"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  Tổng đề phải ≥ 1 câu cho mỗi phần TN/ĐS/TL/TLU. Hệ thống vẫn giữ phân tích AI nhờ metadata difficulty.
                </p>
              </div>
            )}

            {!quizData && (
              <div className="mt-6 rounded-2xl border border-gray-200 p-4 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-2">
                    {(Object.keys(GROUP_LABELS) as ('TN' | 'DS' | 'TL' | 'TLU')[]).map(group => (
                      <span
                        key={group}
                        className="inline-flex items-center px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-sm"
                      >
                        {GROUP_LABELS[group].title}: {planTotals[group]} câu, {formatScore(planScores.perGroup[group])} đ
                      </span>
                    ))}
                  </div>
                  <div className="text-sm font-semibold">
                    Tổng: {formatScore(planScores.total)} / 10
                    {planWarning && (
                      <span className="ml-2 text-red-600 font-normal">• Vượt 10 đ — giảm số câu</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {summary && !quizData && (
              <div className="mt-8 grid lg:grid-cols-3 gap-4">
                {(Object.keys(GROUP_LABELS) as Array<keyof typeof GROUP_LABELS>).map(group => (
                  <div key={group} className="rounded-2xl border border-gray-100 bg-gradient-to-br from-slate-50 to-white p-5 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">
                          {GROUP_LABELS[group].subtitle} • Đang đặt: {planTotals[group]} câu
                        </p>
                        <h4 className="text-lg font-bold text-gray-900">{GROUP_LABELS[group].title}</h4>
                      </div>
                      <div className="text-3xl font-black text-blue-600">{summary[group].count}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      {Object.keys(DIFFICULTY_LABELS).map(diff => (
                        <div key={diff} className="rounded-xl bg-white border border-gray-100 py-2">
                          <p className="text-xs text-gray-500">{DIFFICULTY_LABELS[diff as 'M1' | 'M2' | 'M3']}</p>
                          <p className="text-base font-semibold text-gray-900">
                            {summary[group].difficulties[diff as 'M1' | 'M2' | 'M3']}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {quizData && actualScores && (
              <div className="mt-6 rounded-2xl border border-gray-200 p-4 bg-white">
                <div className="flex flex-wrap gap-2">
                  {(Object.keys(GROUP_LABELS) as ('TN' | 'DS' | 'TL' | 'TLU')[]).map(group => (
                    <span
                      key={group}
                      className="inline-flex items-center px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-sm"
                    >
                      {GROUP_LABELS[group].title}: {actualScores.counts[group]} câu, {formatScore(actualScores.perGroup[group])} đ
                    </span>
                  ))}
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-sm font-semibold text-emerald-700">
                    Tổng: {formatScore(actualScores.total)} / 10
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!showQuiz && !isFullscreen && <Footer />}
    </div>
  )
}

export default DeTuDongPage

